import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import styled from "styled-components";
import { FaFilePdf } from "react-icons/fa";
import "./audio-video-image-dropzone.scss";

interface AudioVideoImageDropzoneProps {
  onImageSelected: (file: File | null) => void;
  base64: string;
  width?: any;
  height?: number;
  uploadMessage?: string;
  defaultFileType?: string;
}

const DropzoneContainer = styled.div<{
  isDragActive: boolean;
  isDragReject: boolean;
  width: number;
  height: number;
}>`
  width: ${({ width }) => `${width}px`};
  height: ${({ height }) => `${height}px`};
  border: 2px dashed #cccccc;
  border-radius: 4px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: ${({ isDragActive, isDragReject }) =>
    isDragActive ? "#e0f7fd" : isDragReject ? "#ffd7d7" : "white"};
  &:hover {
    border-color: #3884cc;
  }
`;

const PreviewContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
`;

const ChangeFileButton = styled.button`
  position: absolute;
  bottom: 5px;
  left: 5px;
  background-color: rgba(255, 255, 255, 0.8);
  border: none;
  padding: 5px 10px;
  cursor: pointer;
`;

const PdfIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 64px;
  color: #d32f2f;
  gap: 10px;
  
  svg {
    font-size: 80px;
  }
`;

const PdfText = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  max-width: 100%;
  word-break: break-all;
  text-align: center;
`;

const AudioVideoImageDropzone: React.FC<AudioVideoImageDropzoneProps> = ({
  onImageSelected,
  base64,
  width = 300,
  height = 300,
  uploadMessage,
  defaultFileType,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(base64);
  const [fileType, setFileType] = useState<string | null>(null);

  useEffect(() => {
    if (previewUrl) {
      if (previewUrl.startsWith("blob:")) {
        return;
      }
      const lowerUrl = previewUrl.toLowerCase();
      const extensionRegex = /\.(\w+)(?:\?.*)?$/;
      const match = lowerUrl.match(extensionRegex);
      if (match) {
        const ext = match[1];
        if (["png", "jpg", "jpeg", "gif"].includes(ext)) {
          setFileType("image");
        } else if (["mp3", "wav", "ogg"].includes(ext)) {
          setFileType("audio");
        } else if (["mp4", "avi", "mov", "webm"].includes(ext)) {
          setFileType("video");
        } else if (["pdf"].includes(ext)) {
          setFileType("pdf");
        } else {
          setFileType("image");
        }
      } else {
        if (defaultFileType) {
          setFileType(defaultFileType);
        } else {
          setFileType("image");
        }
      }
    }
  }, [previewUrl, defaultFileType]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);
        
        // Detecta o tipo de arquivo
        if (file.type === "application/pdf" || file.name.toLowerCase().endsWith('.pdf')) {
          setFileType("pdf");
        } else {
          setFileType(file.type.split("/")[0]);
        }
        
        onImageSelected(file);
      }
    },
    [onImageSelected]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({ onDrop });

  const handleChangeFile = () => {
    setPreviewUrl(null);
    setFileType(null);
    onImageSelected(null);
  };

  // Remover duplicidade de props: width, height, isDragActive, isDragReject
  const rootProps = getRootProps();
  delete rootProps.width;
  delete rootProps.height;
  delete rootProps.isDragActive;
  delete rootProps.isDragReject;

  return (
    <DropzoneContainer
      width={width}
      height={height}
      isDragActive={isDragActive}
      isDragReject={isDragReject}
      {...rootProps}
    >
      <input {...getInputProps()} />
      {previewUrl ? (
        <PreviewContent>
          {fileType === "image" && (
            <PreviewImage src={previewUrl} alt="Preview" />
          )}
          {fileType === "audio" && (
            <audio controls style={{ width: "100%" }} src={previewUrl}>
              Seu navegador não suporta o elemento de áudio.
            </audio>
          )}
          {fileType === "video" && (
            <video
              controls
              style={{ width: "100%", height: "100%" }}
              src={previewUrl}
            >
              Seu navegador não suporta o elemento de vídeo.
            </video>
          )}
          {fileType === "pdf" && (
            <PdfIcon>
              <FaFilePdf />
              <PdfText>Arquivo PDF</PdfText>
            </PdfIcon>
          )}
          <ChangeFileButton onClick={handleChangeFile}>
            Alterar arquivo
          </ChangeFileButton>
        </PreviewContent>
      ) : (
        <p>
          {isDragActive
            ? "Solte o arquivo aqui"
            : uploadMessage ||
              "Arraste ou clique para selecionar um arquivo (imagem, áudio, vídeo ou PDF)."}
        </p>
      )}
    </DropzoneContainer>
  );
};

export { AudioVideoImageDropzone };
